import { Column, createColumnHelper } from "@tanstack/react-table";
import {
  ModelScreenerCellData,
  ModelScreenerRow,
} from "@models/model-screener/entities/modelScreenerData";
import { MSFilterValue } from "@models/model-screener/entities/modelScreenerFilter";
import {
  MSFavouriteFilterValues,
  MSSelectedFilterValues,
} from "@models/model-screener/enums/filterValues";
import { ModelScreenerFieldType } from "@models/model-screener/enums/fieldType";
import { MSTableManagerCell } from "@pages/managed-accounts/model-screener/components/model-screener-table/rendered-fields/manager-cell";
import { MSTableQuickLinkCell } from "./components/model-screener-table/rendered-fields/quick-link-cell";
import { MSTableYesNoCell } from "./components/model-screener-table/rendered-fields/yes-no-cell";
import { MSTableNumberCell } from "./components/model-screener-table/rendered-fields/number-cell";
import { MSTableTextLongCell } from "./components/model-screener-table/rendered-fields/text-long-cell";
import { MSTablePercentageCell } from "./components/model-screener-table/rendered-fields/percentage-cell";
import { MSTableCurrencyCell } from "./components/model-screener-table/rendered-fields/currency-cell";
import { MSTableTextShortCell } from "./components/model-screener-table/rendered-fields/text-short-cell";
import { MemoExoticComponent } from "react";
import { ModelScreenerFilterType } from "@models/model-screener/enums/filterType";
import { MSTableColumnFilter } from "@pages/managed-accounts/model-screener/components/model-screener-table/table/table-head-cell/filter-menu/column-filter";
import { template } from "lodash";

// Table
export const MSColumnHelper = createColumnHelper<ModelScreenerRow>();
export const MSSelectAllRowCheckBoxId = "0";
export const MSDefaultDelayTimeMs = 300;

export class MSTableDefaultSizeConstants {
  static readonly ColumnWidth = 100;
  static readonly CellHeight = 45;
  static readonly FilterIconWidth = 20;
  static readonly HeaderGroupHeight = 82;
  static readonly TopPaddingToTable = 260;
  static readonly BasicUserMessageHeight = 20;
}

export class MSOverScanConstants {
  static readonly DefaultRowOverscan = 1;
  static readonly DefaultColumnOverscan = 2;
  static readonly FireFoxRowOverscan = 20;
  static readonly FireFoxColumnOverscan = 20;
}

export class MSTableStickyConstants {
  static readonly LastBodyColumnIndex = 3;
  static readonly LastMainHeaderColumnIndex = 1;
}

export const MSTableCellComponentMap: Record<
  ModelScreenerFieldType,
  MemoExoticComponent<
    ({ cellData }: { cellData: ModelScreenerCellData }) => JSX.Element
  > | null
> = {
  [ModelScreenerFieldType.Manager]: MSTableManagerCell,
  [ModelScreenerFieldType.Link]: MSTableQuickLinkCell,
  [ModelScreenerFieldType.YesNo]: MSTableYesNoCell,
  [ModelScreenerFieldType.TextLong]: MSTableTextLongCell,
  [ModelScreenerFieldType.Number]: MSTableNumberCell,
  [ModelScreenerFieldType.NumberText]: MSTableNumberCell,
  [ModelScreenerFieldType.Percentage]: MSTablePercentageCell,
  [ModelScreenerFieldType.PercentageText]: MSTablePercentageCell,
  [ModelScreenerFieldType.Currency]: MSTableCurrencyCell,
  [ModelScreenerFieldType.CurrencyText]: MSTableCurrencyCell,
  [ModelScreenerFieldType.DateTime]: MSTableTextShortCell,
  [ModelScreenerFieldType.TextShort]: MSTableTextShortCell,
  [ModelScreenerFieldType.Image]: null,
  [ModelScreenerFieldType.Dropdown]: null,
};

// Data
export const MSDefaultNumericDecimalPlaces = 2;
export const MSDefaultDateFormat = "dd/MM/yy";

export class MSCellDataConstants {
  static readonly Yes = "true";
  static readonly No = "false";
  static readonly NoData = "No data";
  static readonly NAData = "N/A";
  static readonly HiddenData = "Hidden";
}

// Filter values
export class MSFilterConstants {
  static readonly MSColumnFilterAll = "All";

  static readonly MSFavouriteFilters: MSFilterValue[] = [
    { label: "All", value: MSFavouriteFilterValues.All },
    { label: "Favourites", value: MSFavouriteFilterValues.Favourites },
  ];

  static readonly MSSelectedFilters: MSFilterValue[] = [
    { label: "All", value: MSSelectedFilterValues.All },
    { label: "Selected", value: MSSelectedFilterValues.Selected },
  ];
}

// Excel download
export enum MSWorkSheetKey {
  Manager = "ScreenerExcelManager",
  Series = "ScreenerExcelSeries",
  Model = "ScreenerExcelModel",
}

export const MSWorkBookNameByWorkSheetRecord: Record<MSWorkSheetKey, string> = {
  [MSWorkSheetKey.Manager]: "Manager",
  [MSWorkSheetKey.Series]: "Series",
  [MSWorkSheetKey.Model]: "Model",
};

export class MSExcelExportFileNameTemplate {
  static readonly GeneralFileName = "SuitabilityHub Model Screener";
  static readonly ExcelFile = template(
    `${this.GeneralFileName} - <%= analysisName %> <%= date %>.xlsx`,
  );
  static readonly ZipFile = template(`${this.GeneralFileName} <%= date %>.zip`);
}

export class MSExcelDownloadConstants {
  static readonly MaxSelected = 20;
  static readonly DateFormat = "yyyyMMdd";
}

export class MSExcelSectionNames {
  static readonly PlatformAvailability  = "Platform availability"
}

// Word download
export class MSWordDownloadConstants {
  static readonly MaxSelected = 2;
}

// Messages
export class MSMessageTemplates {
  static readonly QuickLinkTooltip = "Click to go to the model details page";
}

export class MSExportMessageTemplates {
  static readonly ExcelDownloadDisclaimer = `Disclaimer
This information is provided for general use only. SuitabilityHub displays information sourced from our users and other third parties, and you must make your own enquiries as to the accuracy and completeness of this information. No representation or warranty (either express or implied) is given as to the truth, accuracy or completeness of the information.

Any results provided by the portfolio returns and risk measures calculator are illustrative only and rely upon information that may change over time. These results should not be relied upon for the purpose of making a decision in relation to a particular financial product, platform or provider. SuitabilityHub does not provide financial advice or make recommendations about the suitability of any financial product, platform, or provider. Any result provided by our calculator is not an offer, and financial product providers will undertake their own fee calculations.

Performance information is sourced directly from product providers, and subject to the provider’s methodology. Past performance is not a reliable indicator of future performance. The information is based on a notional portfolio run on the provider’s nominated platform and does not take into account differences with the actual portfolio implementation. Unless otherwise disclosed, returns are: net of portfolio management fees, performance fees and underlying investment fees; gross of platform fees; before taxes; and assume reinvestment of distributions.

Returns may vary across platforms due to factors including differences in platform fees, the timing and implementation of trades, and individual investor circumstances.`;
  static readonly SeeWebVersionForDetails =
    "SuitabilityHub portfolio analysis disclaimers and assumptions apply. See web version for details";
}

export class MSWorkSheetStyleConstants {
  static readonly DefaultColumnWidth = 48;
  static readonly DefaultFontSize = 11;
  static readonly RichTextFontSize = 10;
  static readonly AnalysisNameRowHeight = 30;
  static readonly MainProductNameRowHeight = 45;
  static readonly DisclaimerSectionColSpan = 4;
}

export const MSTableFilterComponentMap: Record<
  ModelScreenerFilterType,
  ({ column }: { column: Column<ModelScreenerRow, any> }) => JSX.Element | null
> = {
  [ModelScreenerFilterType.Checkbox]: MSTableColumnFilter,
  [ModelScreenerFilterType.Column]: MSTableColumnFilter,
  [ModelScreenerFilterType.ViaSearch]: () => null,
  [ModelScreenerFilterType.TrackRecord]: () => null,
  [ModelScreenerFilterType.Range]: () => null,
  [ModelScreenerFilterType.ViaMainFilter]: () => null,
  [ModelScreenerFilterType.Custom]: () => null,
  [ModelScreenerFilterType.None]: () => null,
};

export class MSSubHeaderIdConstants {
  static readonly ModelManager = "ModelManager";
  static readonly ModelSeries = "ModelSeries";
  static readonly ModelModel = "ModelModel";
  static readonly InvestmentUniverseOther = "InvestmentUniverseOther";
}

export const ModelScreenerSpecificColumnSizes: Record<string, number> = {
  "BT Panorama": 120,
}
